/**
 * floatingVueObserver — THE BRIDGE THAT PUTS floating-vue'S MENUS IN THE LEDGER.
 *
 * floating-vue is bridged, never replaced (design D4): every `v-tooltip`,
 * `VDropdown` and `VMenu` site in the app keeps its call exactly as written, and
 * this module is the whole of what the overlay primitive knows about them. It
 * watches the document for poppers floating-vue shows and registers the
 * menu-like ones in `overlayRegistry.js`, so a panel that opens beside an open
 * right-click menu is placed clear of it (`avoidOverlays`, D3).
 *
 * WHY AN OBSERVER AND NOT A CALL SITE. The adoption line the founder re-ruled
 * leaves every surface floating-vue hosts ON floating-vue. There is no
 * `useOverlay` call to add, and there are ~100 sites, so the registration has to
 * come from the one thing they all share: the popper node floating-vue mounts
 * on `body`. No app site sets the plugin's `container`, so every popper is a
 * body descendant and one observer sees all of them.
 *
 * THE RULE, IN TWO LINES.
 *   A node carrying `v-popper__popper--shown` AND an obstacle theme class
 *   (`obstacleThemeClasses()`) is registered as kind `floating-vue-popper`.
 *   It is deregistered the moment it loses `--shown` or leaves the DOM.
 *
 * WHY THE CLASS AND NOT A LIFECYCLE HOOK. floating-vue appends a popper to
 * `body` BEFORE it is shown — the childList mutation arrives with the node
 * already `--hidden` — and toggles `--shown`/`--hidden` on that same node
 * afterwards. So the append is not the signal; the class is. On the way out the
 * class flips first and the node is detached ~150 ms later (`disposeTimeout`),
 * which is why the entry has to go on `--hidden` rather than on removal: a menu
 * that closed must stop shoving panels immediately, not a sixth of a second
 * later. Removal is still handled, for the case where the node is torn down
 * while shown.
 *
 * A HOVER TOOLTIP IS NOT AN OBSTACLE. The `tooltip` theme extends neither
 * `dropdown` nor `menu`, so it never matches, and a tooltip drifting under the
 * pointer can never re-place an open panel. That asymmetry is the whole reason
 * the theme set is consulted at all.
 *
 * A SHOWN MENU TAKES ITS TURN IN ESCAPE ORDER (th-imasv). floating-vue binds
 * no keydown anywhere; its one Escape is `@keyup.esc` on the popper root, gated
 * on `autoHide`, so it is heard only on the key's RELEASE and only while focus
 * sits inside the popper. The Escape contract dispatches on keyDOWN, before
 * that, and would hand the press to whatever layer the menu opened over. So
 * the entry is an Escape registrant like any other overlay, on the one
 * dispatcher, in activation order, and its `dismiss` delivers that same keyup
 * to the root: the hide runs floating-vue's own path, and an owner controlling
 * the popper through `:shown` hears its `hide` / `update:shown`. It claims the
 * key only while floating-vue would close the popper on it, which the root
 * renders as its `tabindex` (both are floating-vue's `autoHide`), and never
 * twice: a popper still fading out through a hide delay has had its Escape,
 * and the next press belongs to the surface beneath.
 *
 * COST. Attribute observation is filtered to `class`; the callback returns on
 * its first line for any node without `v-popper__popper`, which is every
 * mutation the app makes that is not a popper.
 *
 * @see openspec/changes/overlay-primitive/design.md — D4, and stress-test Blind Spot 2
 * @see ./floatingVueThemes.js — which themes count as menus, derived from the declarations
 */

import { obstacleThemeClasses } from "./floatingVueThemes";
import { register } from "./overlayRegistry";

/** floating-vue's popper root, and the shown flag it toggles on that same node. */
const POPPER_CLASS = "v-popper__popper";
const SHOWN_CLASS = "v-popper__popper--shown";
const POPPER_SELECTOR = `.${POPPER_CLASS}`;

/**
 * `attributeFilter` is what keeps a subtree observer affordable: without it
 * every `class` change in the application — every Tailwind state toggle — would
 * wake this callback, and so would every other attribute besides.
 */
const OBSERVER_OPTIONS = Object.freeze({
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ["class"],
});

/** Is this a floating-vue popper root? False for text nodes and everything else. */
function isPopper(node) {
  return Boolean(node?.classList?.contains(POPPER_CLASS));
}

/**
 * The poppers at or under a node. A childList record names only the subtree's
 * ROOT, so an added or removed container has to be walked — `querySelectorAll`
 * works on a detached subtree, which is what makes the removal case reachable.
 */
function poppersWithin(node) {
  const found = [];
  if (isPopper(node)) found.push(node);
  if (typeof node?.querySelectorAll === "function") {
    found.push(...node.querySelectorAll(POPPER_SELECTOR));
  }
  return found;
}

/**
 * Start watching for shown menu poppers and registering them as obstacles.
 * Called ONCE, from `entrypoints/application.js`, after the app mounts — it
 * observes the document rather than the app, so what it needs is a DOM.
 *
 * @param {HTMLElement} [root] - the subtree to watch; `document.body` in the app.
 * @returns {() => void} uninstall: stop observing and release every entry it holds.
 */
export function installFloatingVueObserver(root = document.body) {
  if (!root || typeof MutationObserver === "undefined") return () => {};

  const obstacleClasses = obstacleThemeClasses();

  /**
   * node → the registry handle held for it. A WeakMap, so a popper floating-vue
   * detached and dropped is not kept alive by this bridge, and so registration
   * is idempotent per node: the class churn floating-vue performs on a shown
   * popper (transition flags, placement data) must not register it twice.
   */
  const handles = new WeakMap();

  const wearsObstacleTheme = (node) =>
    obstacleClasses.some((className) => node.classList.contains(className));

  function remember(node) {
    if (handles.has(node)) return;
    // Set by this popper's one Escape; a re-show registers afresh.
    let dismissed = false;
    const handle = register({
      kind: "floating-vue-popper",
      // Read at placement time, which is the only moment a rect is true. The
      // roster declares this kind `placed: false`: floating-vue positions it,
      // the registry only stacks it and reports its box.
      rect: () => node.getBoundingClientRect(),
      // Re-read at dispatch (header, A SHOWN MENU TAKES ITS TURN).
      active: () => !dismissed && node.hasAttribute("tabindex"),
      // Non-bubbling: it reaches the root's own listener and no bubble-phase
      // keyup elsewhere, which hears the real release as it always has.
      dismiss: () => {
        dismissed = true;
        node.dispatchEvent(new KeyboardEvent("keyup", { key: "Escape" }));
      },
    });
    // `register` answers `null` for a kind the roster does not declare (a
    // production build logs and refuses). Nothing is held, so nothing is freed.
    if (handle) handles.set(node, handle);
  }

  function forget(node) {
    const handle = handles.get(node);
    if (!handle) return;
    handle.deregister();
    handles.delete(node);
  }

  function sync(node) {
    if (!isPopper(node)) return;
    if (node.classList.contains(SHOWN_CLASS) && wearsObstacleTheme(node)) remember(node);
    else forget(node);
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === "childList") {
        // Removals first: a record that replaces a node carries both, and the
        // outgoing entry must not outlive its replacement's registration.
        for (const removed of record.removedNodes) poppersWithin(removed).forEach(forget);
        for (const added of record.addedNodes) poppersWithin(added).forEach(sync);
      } else {
        sync(record.target);
      }
    }
  });

  // A popper already shown when the observer installs — the app mounts after
  // floating-vue is installed, and a test may seed one — is state the observer
  // would otherwise never be told about, because no mutation is coming for it.
  poppersWithin(root).forEach(sync);
  observer.observe(root, OBSERVER_OPTIONS);

  return function uninstall() {
    observer.disconnect();
    // Every node still held is by definition still attached: removal
    // deregisters, and the observer ran until this line. So the live sweep is a
    // complete release, and the ledger is left as it was before the install.
    poppersWithin(root).forEach(forget);
  };
}
